'use client'

import type { DeviceType } from '@repo/shared/utils/detect-device'
import { memo, useEffect, useMemo, useState } from 'react'

interface ClientDeviceWrapperProps {
  initialDevice: DeviceType
  mobile: React.ReactNode
  tablet: React.ReactNode
  desktop: React.ReactNode
}

const ClientDeviceWrapper = memo(function ClientDeviceWrapper({
  initialDevice,
  mobile,
  tablet,
  desktop,
}: ClientDeviceWrapperProps) {
  const [device, setDevice] = useState<DeviceType>(initialDevice)

  const getDeviceFromWidth = useMemo(() => {
    return (width: number): DeviceType => {
      if (width < 768) return 'mobile'
      if (width < 1024) return 'tablet'
      return 'desktop'
    }
  }, [])

  useEffect(() => {
    let resizeListener: (() => void) | null = null

    const updateDeviceFromClient = () => {
      const newDevice = getDeviceFromWidth(window.innerWidth)
      // Only update state if device type actually changed
      if (newDevice !== device) {
        setDevice(newDevice)
      }
    }

    const timer = setTimeout(() => {
      updateDeviceFromClient()
      resizeListener = () => updateDeviceFromClient()
      window.addEventListener('resize', resizeListener!)
    }, 2000)

    return () => {
      clearTimeout(timer)
      if (resizeListener) {
        window.removeEventListener('resize', resizeListener)
      }
    }
  }, [device])

  const renderedContent = useMemo(() => {
    if (device === 'mobile') return mobile
    if (device === 'tablet') return tablet
    return desktop
  }, [device, mobile, tablet, desktop])

  return renderedContent ? renderedContent : undefined
})

export default ClientDeviceWrapper
